import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../Settings/appSettings';
import { toThumbnailUrl } from '../utils/imageUtils';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface LibraryImage {
    _id: string;
    url: string;
    fileName: string;
    displayName: string;
    uploadDate: string;
    metadata?: { size?: number; contentType?: string };
}

interface DeleteReference {
    id: string;
    name: string;
    type: string;
    collection: string;
}

/** Props shared between the content component and the dialog wrapper. */
export interface ImageLibraryContentProps {
    /** readonly – browse only; select – click to pick; edit – rename + delete */
    mode: 'readonly' | 'select' | 'edit';
    onSelect?: (image: Pick<LibraryImage, 'url' | 'displayName'>) => void;
    /**
     * Optional: called when the user triggers a "close/cancel" action from
     * within the content (e.g. Cancel button in select mode).
     * Not needed for inline/embedded usage.
     */
    onClose?: () => void;
}

export interface ImageLibraryProps extends ImageLibraryContentProps {
    open: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CARD_SIZE = 160;

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
        });
    } catch {
        return '';
    }
}

/** Strip the {userID}- prefix that the server prepends to blob names. */
function resolveDisplayName(image: LibraryImage): string {
    if (image.displayName) return image.displayName;
    const dash = image.fileName.indexOf('-');
    return dash !== -1 ? image.fileName.slice(dash + 1) : image.fileName;
}

function referenceLabel(ref: DeleteReference): string {
    switch (ref.type) {
        case 'event-card':  return `${ref.name} (event card image)`;
        case 'score-card':  return `${ref.name} (score card template)`;
        case 'both':        return `${ref.name} (event card + score card template)`;
        case 'group-card':  return `${ref.name} (group card image)`;
        default:            return ref.name;
    }
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ImageCardProps {
    image: LibraryImage;
    mode: ImageLibraryContentProps['mode'];
    onPreview: (img: LibraryImage) => void;
    onSelect?: (img: LibraryImage) => void;
    onRenameStart: (img: LibraryImage) => void;
    onDeleteStart: (img: LibraryImage) => void;
}

const ImageCard = React.memo(({ image, mode, onPreview, onSelect, onRenameStart, onDeleteStart }: ImageCardProps) => {
    const isClickable = mode === 'select' || mode === 'readonly' || mode === 'edit';
    const [imgError, setImgError] = useState(false);
    const displayName = resolveDisplayName(image);

    const handleClick = () => {
        if (imgError) return;
        if (mode === 'select' && onSelect) {
            onSelect(image);
        } else if (mode === 'readonly' || mode === 'edit') {
            onPreview(image);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: imgError ? 'error.light' : 'divider',
                backgroundColor: 'background.paper',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                cursor: mode === 'select' && !imgError ? 'pointer' : 'default',
                '&:hover': isClickable && !imgError ? {
                    boxShadow: 3,
                    borderColor: 'primary.main',
                } : {},
            }}
        >
            {/* Image area — square, fills full card width */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '1 / 1',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    cursor: mode === 'select' && !imgError ? 'pointer' : imgError ? 'default' : 'zoom-in',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    '&:hover .card-overlay': { opacity: imgError ? 0 : 1 },
                }}
                onClick={handleClick}
            >
                {imgError ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        color: 'text.disabled',
                    }}>
                        <BrokenImageOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                        <Typography variant="caption" color="text.disabled">
                            Unavailable
                        </Typography>
                    </Box>
                ) : (
                <Box
                    component="img"
                    src={toThumbnailUrl(image.url, CARD_SIZE)}
                    alt={displayName}
                    loading="lazy"
                    onError={() => setImgError(true)}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
                )}
                {/* Hover overlay */}
                {!imgError && (
                    <Box
                        className="card-overlay"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0,0,0,0.35)',
                            opacity: 0,
                            transition: 'opacity 0.15s',
                        }}
                    >
                        {mode === 'select' ? (
                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                                Select
                            </Typography>
                        ) : (
                            <ZoomInIcon sx={{ color: '#fff', fontSize: 32 }} />
                        )}
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1, pt: 0.75, pb: 0.5, minWidth: 0 }}>
                <Tooltip title={displayName} placement="top" enterDelay={600}>
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: CARD_SIZE - 8,
                            color: imgError ? 'text.disabled' : 'text.primary',
                        }}
                    >
                        {displayName}
                    </Typography>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {formatDate(image.uploadDate)}
                </Typography>

                {mode === 'edit' && (
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.25 }}>
                        <Tooltip title="Rename">
                            <IconButton size="small" onClick={() => onRenameStart(image)}>
                                <DriveFileRenameOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDeleteStart(image)}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                )}
            </Box>
        </Box>
    );
});

// ── Content component (inline-embeddable) ─────────────────────────────────────

/**
 * The inner image library UI: toolbar (search + upload), image grid, lightbox,
 * and delete-confirm dialog. Can be used standalone (embedded in a page section)
 * or inside the `ImageLibrary` dialog wrapper.
 */
export const ImageLibraryContent = ({ mode, onSelect, onClose }: ImageLibraryContentProps) => {
    const api = CreateApi();
    const auth = useAuth();
    const userID = auth.user?.profile?.sub ?? '';

    const [images, setImages] = useState<LibraryImage[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Upload
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    // Lightbox
    const [lightboxImage, setLightboxImage] = useState<LibraryImage | null>(null);

    // Rename
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);
    const renameInputRef = useRef<HTMLInputElement>(null);

    // Delete
    const [deleteTarget, setDeleteTarget] = useState<LibraryImage | null>(null);
    const [deleteRefs, setDeleteRefs] = useState<DeleteReference[]>([]);
    const [deleteChecking, setDeleteChecking] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // ── Load images on mount (or when userID becomes available) ───────────────

    useEffect(() => {
        if (!userID) return;
        setLoading(true);
        api.get(`/image/getUserImages?userID=${encodeURIComponent(userID)}`)
            .then((res) => setImages(res.data))
            .catch((err) => console.error('Failed to load images', err))
            .finally(() => setLoading(false));
    }, [userID]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Filtered list ─────────────────────────────────────────────────────────

    const filtered = search.trim()
        ? images.filter((img) =>
            resolveDisplayName(img).toLowerCase().includes(search.trim().toLowerCase())
        )
        : images;

    // ── Select ────────────────────────────────────────────────────────────────

    const handleSelect = useCallback((img: LibraryImage) => {
        onSelect?.({ url: img.url, displayName: img.displayName });
        onClose?.();
    }, [onSelect, onClose]);

    // ── Upload ────────────────────────────────────────────────────────────────

    const handleUploadClick = () => uploadInputRef.current?.click();

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !userID) return;
        e.target.value = '';

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file, file.name);
            formData.append('userID', userID);
            formData.append('_id', '');

            const res = await api.post('/image/uploadEventLogo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const newImage: LibraryImage = {
                _id: res.data.imageId,
                url: res.data.imageUrl,
                fileName: `${userID}-${file.name}`,
                displayName: file.name,
                uploadDate: new Date().toISOString(),
                metadata: { size: file.size, contentType: file.type },
            };

            setImages((prev) => [newImage, ...prev]);
        } catch (err) {
            console.error('Upload failed', err);
        } finally {
            setUploading(false);
        }
    };

    // ── Rename ────────────────────────────────────────────────────────────────

    const handleRenameStart = (img: LibraryImage) => {
        setRenamingId(img._id);
        setRenameValue(img.displayName);
        setTimeout(() => renameInputRef.current?.focus(), 50);
    };

    const handleRenameCommit = async (img: LibraryImage) => {
        const newName = renameValue.trim();
        if (!newName || newName === img.displayName) {
            setRenamingId(null);
            return;
        }
        setRenaming(true);
        try {
            await api.put('/image/renameImage', { imageId: img._id, displayName: newName });
            setImages((prev) =>
                prev.map((i) => (i._id === img._id ? { ...i, displayName: newName } : i))
            );
        } catch (err) {
            console.error('Rename failed', err);
        } finally {
            setRenaming(false);
            setRenamingId(null);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────

    const handleDeleteStart = async (img: LibraryImage) => {
        setDeleteTarget(img);
        setDeleteChecking(true);
        try {
            const res = await api.delete('/image/deleteImage', { data: { imageId: img._id } });
            setDeleteRefs(res.data.references ?? []);
        } catch (err) {
            console.error('Reference check failed', err);
            setDeleteRefs([]);
        } finally {
            setDeleteChecking(false);
            setDeleteConfirmOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await api.delete('/image/deleteImage?confirm=true', { data: { imageId: deleteTarget._id } });
            setImages((prev) => prev.filter((i) => i._id !== deleteTarget._id));
            setDeleteConfirmOpen(false);
            setDeleteTarget(null);
            setDeleteRefs([]);
        } catch (err) {
            console.error('Delete failed', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setDeleteTarget(null);
        setDeleteRefs([]);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <>
            {/* Hidden file input — triggered by the Upload button */}
            <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            {/* Toolbar: search + upload */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                <TextField
                    size="small"
                    placeholder="Search by name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: 1, maxWidth: 320 }}
                />
                {mode !== 'readonly' && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={uploading
                            ? <CircularProgress size={14} color="inherit" />
                            : <FileUploadOutlinedIcon fontSize="small" />
                        }
                        onClick={handleUploadClick}
                        disabled={uploading}
                    >
                        {uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                )}
            </Stack>

            {/* Image grid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                        {search.trim() ? 'No images match your search.' : 'No images uploaded yet.'}
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_SIZE}px, 1fr))`,
                        gap: 2,
                        overflowY: 'auto',
                    }}
                >
                    {filtered.map((img) =>
                        renamingId === img._id ? (
                            // Inline rename card
                            <Box
                                key={img._id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                    backgroundColor: 'background.paper',
                                }}
                            >
                                <Box sx={{
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.03)',
                                    overflow: 'hidden',
                                }}>
                                    <Box
                                        component="img"
                                        src={toThumbnailUrl(img.url, CARD_SIZE)}
                                        alt={resolveDisplayName(img)}
                                        loading="lazy"
                                        sx={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                                    />
                                </Box>
                                <Box sx={{ px: 1, pt: 0.75, pb: 0.75 }}>
                                    <TextField
                                        inputRef={renameInputRef}
                                        size="small"
                                        fullWidth
                                        value={renameValue}
                                        disabled={renaming}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onBlur={() => handleRenameCommit(img)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleRenameCommit(img);
                                            if (e.key === 'Escape') setRenamingId(null);
                                        }}
                                        InputProps={{
                                            endAdornment: renaming ? (
                                                <InputAdornment position="end">
                                                    <CircularProgress size={14} />
                                                </InputAdornment>
                                            ) : undefined,
                                        }}
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <ImageCard
                                key={img._id}
                                image={img}
                                mode={mode}
                                onPreview={setLightboxImage}
                                onSelect={handleSelect}
                                onRenameStart={handleRenameStart}
                                onDeleteStart={handleDeleteStart}
                            />
                        )
                    )}
                </Box>
            )}

            {/* Lightbox — full resolution, loaded on demand */}
            <Dialog
                open={!!lightboxImage}
                onClose={() => setLightboxImage(null)}
                maxWidth="lg"
                PaperProps={{ sx: { backgroundColor: '#fff', borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {lightboxImage ? resolveDisplayName(lightboxImage) : ''}
                    </Typography>
                    <IconButton size="small" onClick={() => setLightboxImage(null)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.03)' }}>
                    {lightboxImage && (
                        <Box
                            component="img"
                            src={lightboxImage.url}
                            alt={resolveDisplayName(lightboxImage)}
                            sx={{ maxWidth: '80vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 1 }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteOutlineIcon color="error" />
                    Delete Image
                </DialogTitle>
                <DialogContent>
                    {deleteChecking ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            <Typography variant="body2">
                                Are you sure you want to permanently delete{' '}
                                <strong>{deleteTarget ? resolveDisplayName(deleteTarget) : ''}</strong>?
                            </Typography>
                            {deleteRefs.length > 0 && (
                                <>
                                    <Typography variant="body2" color="error" fontWeight={600}>
                                        Warning: the following events or groups use this image and will no longer display it after deletion:
                                    </Typography>
                                    <List dense disablePadding>
                                        {deleteRefs.map((ref) => (
                                            <ListItem key={ref.id} disableGutters sx={{ py: 0 }}>
                                                <ListItemText
                                                    primary={referenceLabel(ref)}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={deleteChecking || deleting}
                        startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ── Dialog wrapper ────────────────────────────────────────────────────────────

/**
 * The image library as a modal dialog. Wraps `ImageLibraryContent` with a
 * titled dialog that includes a mode chip and a close button.
 */
export const ImageLibrary = ({ open, onClose, mode, onSelect }: ImageLibraryProps) => {
    const modeLabel = mode === 'readonly' ? 'Browse' : mode === 'select' ? 'Select an Image' : 'Manage';
    const modeColor = mode === 'edit' ? 'warning' : mode === 'select' ? 'primary' : 'default';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { minHeight: '70vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Image Library
                </Typography>
                <Chip label={modeLabel} color={modeColor} size="small" />
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ImageLibraryContent mode={mode} onSelect={onSelect} onClose={onClose} />
            </DialogContent>

            {mode === 'select' && (
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
